import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useCurrentCountry, useMatchSmallViewport } from '@avensia/excite';
import Image from 'webapp/components/image';
import { Theme } from 'webapp/theme';
import Select from 'svenssons/shared/fields/Select';
import Price from 'svenssons/product/Price';
import { MobileVariantSelector } from './MobileVariantSelector';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { StockStatus } from 'svenssons/product/StockStatus';
import { useVariantSelector, type OptionType } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';
import { primary } from 'svenssons/shared/theme';
import { addSelectItemEventToDataLayer } from 'webapp/tracking/events/items';
import { Variant } from 'webapp/product/price-variant';
import { FieldSize } from 'webapp/components/field-size';
import Link from 'webapp/components/Link';
import { NnTestIdProps, NNTestValueProp } from 'webapp/shared/test-ids';

export function VariantTransform(option: OptionType, isSelectedValue: boolean, placeholder?: string) {
  const currentCountry = useCurrentCountry();
  const variant = option.variant;

  const onClick = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    variant &&
      addSelectItemEventToDataLayer({ product: variant, list_id: 'variant_selector', list_name: 'variant_selector' });
  };

  return isSelectedValue ? (
    <SelectedOption to={variant?.url} onClick={e => e.preventDefault()}>
      <VariantImage image={option.image ?? variant?.image} preset={'thumb'} />
      <styled.Span css={overflowEllipsisDecorator}>
        {placeholder ? `${placeholder}: ${option.text}` : option.text}
      </styled.Span>
    </SelectedOption>
  ) : (
    <Option to={variant?.url} onClick={onClick} {...NNTestValueProp(variant?.articleNumber)}>
      <SelectedVariantImage image={option.image ?? variant?.image} preset={'thumb'} />
      <Text>{option.text}</Text>
      <Info>
        {variant?.availableInCountry && variant && variant?.price.inclVat !== 0 && (
          <CurrentPrice>
            <Price
              showPlusSign={option.showPlusSign}
              variant={Variant.Compact}
              price={{ current: variant?.price } as NordicNest.Api.PriceApiModel}
            />
          </CurrentPrice>
        )}
        {variant?.availableInCountry && variant?.stockStatus && variant.variantStatus ? (
          <StockStyle variantStatus={variant.variantStatus} stockStatus={variant.stockStatus} />
        ) : null}
        {!variant?.availableInCountry && (
          <NotAvailableInCountry>
            {translate(t => t.productPage.productNotAvailableInCountry, { ['country']: currentCountry.localName })}
          </NotAvailableInCountry>
        )}
      </Info>
    </Option>
  );
}

const CurrentPrice = styled.span({
  fontSize: t => t.sharedFontSize.xs,
  display: 'flex',
  alignItems: 'center',
});

export function productConfigurationTransform(
  selectedOption: NordicNest.Api.SelectOptionApiModel,
  feature: NordicNest.Api.FeatureApiModel,
  isSelectedValue: boolean,
) {
  const option = feature.options.find(o => o.id === selectedOption.value);
  return isSelectedValue ? (
    <SelectedOption>
      <VariantImage image={{ url: option?.icon ?? '' }} />
      <styled.Span css={overflowEllipsisDecorator}>{feature.label + ': ' + selectedOption.text}</styled.Span>
    </SelectedOption>
  ) : (
    <Option>
      <SelectedVariantImage image={{ url: option?.icon ?? '' }} />
      <styled.Span css={overflowEllipsisDecorator}>{selectedOption.text}</styled.Span>
      <Info></Info>
    </Option>
  );
}

export default React.memo(
  styled(function ProductVariantSelector(props: { selectorId?: string; onRemoveOption?: () => void }) {
    const isSmallViewport = useMatchSmallViewport();
    const { selectors, open, selectOption } = useVariantSelector(props.selectorId);
    const displayedSelectors =
      props.selectorId === undefined ? selectors : selectors.filter(s => s.id === props.selectorId);
    return (
      <>
        {displayedSelectors.map(
          ({
            options,
            optionTransform,
            selectedTransform,
            selectedOption,
            value,
            placeholder,
            showVariantSelect,
            id,
          }) =>
            showVariantSelect ? (
              <React.Fragment key={value + id}>
                {isSmallViewport ? (
                  <MobileVariantSelector
                    open={open}
                    selectedTransform={selectedTransform}
                    optionTransform={optionTransform}
                    value={value}
                    css={marginDecorator}
                    id={id}
                    selectedOption={selectedOption}
                    placeholder={placeholder}
                    onRemoveOption={props.onRemoveOption}
                    {...NnTestIdProps.ProductVariationSelector}
                  />
                ) : (
                  <VariantSelector
                    as={Theme.White}
                    size={FieldSize.Small}
                    options={options}
                    onOpen={() => open(id)}
                    onChangeOption={selectOption}
                    selectedTransform={selectedTransform}
                    optionTransform={optionTransform}
                    value={value}
                    css={marginDecorator}
                    placeholder={placeholder}
                    onRemoveOption={props.onRemoveOption}
                    selectedValueDecorator={paddingDecorator}
                    optionDecorator={paddingDecorator}
                    {...NnTestIdProps.ProductVariationSelector}
                  />
                )}
              </React.Fragment>
            ) : null,
        )}
      </>
    );
  }),
);

const paddingDecorator = styled({
  padding: { xy: t => t.sharedSpacing.s },
});

const Text = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  whiteSpace: 'break-spaces',
  flexShrink: 0,
});

const Info = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-around',
  alignItems: 'flex-end',
});

const VariantImage = styled(Image, {
  width: 40,
  height: 40,
  objectFit: 'contain',
});

const SelectedVariantImage = styled(
  VariantImage,
  createMaxSmallViewportDecorator({
    mixBlendMode: 'multiply',
  }),
);

const StockStyle = styled(StockStatus, {
  fontWeight: '400',
});

const marginDecorator = styled({
  marginBottom: t => t.sharedSpacing.xl,
});

const VariantSelector = styled(Select, {
  display: 'flex',
});

const Option = styled(
  Link,
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.s,
  }),
  {
    fontSize: t => t.sharedFontSize.xs,
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto',
    alignItems: 'center',
    gap: t => t.sharedSpacing.m,
  },
);

const optionDecrator = styled({
  fontSize: t => t.sharedFontSize.xs,
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  ':hover': {
    textDecoration: 'none',
  },
});

const SelectedOption = styled(Link, optionDecrator);

const NotAvailableInCountry = styled.div({
  fontSize: t => t.sharedFontSize.xxs,
  whiteSpace: 'break-spaces',
  textAlign: 'right',
  lineHeight: 'normal',
  fontWeight: 400,
  color: primary,
});
